import type { ReactElement } from 'react';
import { Box, Checkbox, useTheme } from '@mui/material';
import {
  GridActionsCellItem,
  type GridActionsColDef,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowParams,
  type GridValidRowModel,
  type ValueOptions,
} from '@mui/x-data-grid';
import { HFChip } from '../../atoms/HFChip';
import { contrastTextColor } from '../../utils/contrast';
import { applyMask, formatCurrency, formatDate, formatNumber, formatPercent, formatRatio, toCellString, type DateStyle } from '../../utils/format';
import type { HFColumnSize, HFGridColDef } from './columnSizing';

// Formatters live in utils/format; re-exported here so table consumers keep one import path.
export {
  EM_DASH,
  applyMask,
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
  formatRatio,
  type CurrencyFormatOptions,
  type DateStyle,
} from '../../utils/format';

// Sign coloring (classes styled by the HFDataTable grid sx)

export type SignClass = 'pos' | 'neg' | 'blank';

export const SIGN_CELL_CLASS = {
  pos: 'hf-cell-pos',
  neg: 'hf-cell-neg',
  blank: 'hf-cell-blank',
} as const;

/** Classify a numeric cell for gain/loss coloring. */
export function classifySign(value: unknown): SignClass {
  if (value === null || value === undefined || value === '') return 'blank';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 'blank';
  if (n > 0) return 'pos';
  if (n < 0) return 'neg';
  return 'blank';
}

/** DataGrid `cellClassName` helper for signed numeric columns. */
export function getSignCellClassName(value: unknown): string {
  return SIGN_CELL_CLASS[classifySign(value)];
}

// Column factories

export type TextColumnOptions<R extends GridValidRowModel> = Partial<Omit<HFGridColDef<R>, 'field' | 'headerName'>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  hfSize?: HFColumnSize;
};

/** Left-aligned text / label column (auto-sized unless `width` / `flex` / `hfSize` opts out). */
export function textColumn<R extends GridValidRowModel>(options: TextColumnOptions<R>): HFGridColDef<R> {
  const { field, headerName, ...rest } = options;
  return {
    field,
    headerName,
    headerAlign: 'left',
    align: 'left',
    minWidth: 120,
    ...rest,
  };
}

export type NumberColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  fractionDigits?: number;
};

/** Right-aligned numeric column with fixed fraction digits. */
export function numberColumn<R extends GridValidRowModel>(options: NumberColumnOptions<R>): GridColDef<R> {
  const { field, headerName, fractionDigits = 2, valueFormatter, ...rest } = options;
  return {
    field,
    headerName,
    type: 'number',
    headerAlign: 'left',
    align: 'right',
    valueFormatter: valueFormatter ?? ((value: unknown) => formatNumber(value, fractionDigits)),
    ...rest,
  };
}

export type PercentColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  fractionDigits?: number;
};

/** Right-aligned percent column without sign coloring. */
export function percentColumn<R extends GridValidRowModel>(options: PercentColumnOptions<R>): GridColDef<R> {
  const { field, headerName, fractionDigits = 2, valueFormatter, ...rest } = options;
  return {
    field,
    headerName,
    type: 'number',
    headerAlign: 'left',
    align: 'right',
    valueFormatter: valueFormatter ?? ((value: unknown) => formatPercent(value, fractionDigits)),
    ...rest,
  };
}

export type SignedPercentColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  fractionDigits?: number;
};

/** Right-aligned percent column with green/red sign cell classes. */
export function signedPercentColumn<R extends GridValidRowModel>(options: SignedPercentColumnOptions<R>): GridColDef<R> {
  const { field, headerName, fractionDigits = 2, valueFormatter, cellClassName, ...rest } = options;
  return {
    field,
    headerName,
    type: 'number',
    headerAlign: 'left',
    align: 'right',
    valueFormatter: valueFormatter ?? ((value: unknown) => formatPercent(value, fractionDigits)),
    cellClassName: cellClassName ?? ((params) => getSignCellClassName(params.value)),
    ...rest,
  };
}

export type RatioColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  fractionDigits?: number;
  /** When true, apply green/red sign cell classes. */
  signed?: boolean;
};

/** Right-aligned ratio column (default 4 decimal places). */
export function ratioColumn<R extends GridValidRowModel>(options: RatioColumnOptions<R>): GridColDef<R> {
  const { field, headerName, fractionDigits = 4, signed = false, valueFormatter, cellClassName, ...rest } = options;
  return {
    field,
    headerName,
    type: 'number',
    headerAlign: 'left',
    align: 'right',
    valueFormatter: valueFormatter ?? ((value: unknown) => formatRatio(value, fractionDigits)),
    cellClassName: cellClassName ?? (signed ? (params) => getSignCellClassName(params.value) : undefined),
    ...rest,
  };
}

export type CurrencyColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  currency?: string;
  fractionDigits?: number;
  locale?: string;
  /** Color positive/negative amounts with sign cell classes. */
  signed?: boolean;
};

/** Right-aligned currency column (`$1,234.50`), auto-sized unless `width` / `flex` is passed. */
export function currencyColumn<R extends GridValidRowModel>(options: CurrencyColumnOptions<R>): GridColDef<R> {
  const {
    field,
    headerName,
    currency = 'USD',
    fractionDigits = 2,
    locale = 'en-US',
    signed = false,
    valueFormatter,
    cellClassName,
    ...rest
  } = options;

  return {
    field,
    headerName,
    type: 'number',
    headerAlign: 'left',
    align: 'right',
    valueFormatter: valueFormatter ?? ((value: unknown) => formatCurrency(value, { currency, fractionDigits, locale })),
    cellClassName: cellClassName ?? (signed ? (params) => getSignCellClassName(params.value) : undefined),
    ...rest,
  };
}

export type DateColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  /** Display style. Defaults to `dd/mm/yyyy`. */
  dateStyle?: DateStyle;
};

function toDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const date = new Date(value as string | number);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Date column with calendar filter operators (`type: 'date'`).
 * Values are normalized to `Date` so sorting/filtering work correctly.
 */
export function dateColumn<R extends GridValidRowModel>(options: DateColumnOptions<R>): GridColDef<R> {
  const { field, headerName, dateStyle = 'dd/mm/yyyy', valueFormatter, valueGetter, ...rest } = options;

  return {
    field,
    headerName,
    type: 'date',
    valueGetter: valueGetter ?? ((_value, row) => toDate((row as Record<string, unknown>)[field])),
    valueFormatter: valueFormatter ?? ((value: unknown) => formatDate(value, dateStyle)),
    ...rest,
  };
}

export type MaskedColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  /** Digit mask where `#` is replaced by the next digit (e.g. `(###) ###-####`). */
  mask: string;
};

/** Text column that formats values through a digit mask (phone, IDs, etc.). */
export function maskedColumn<R extends GridValidRowModel>(options: MaskedColumnOptions<R>): GridColDef<R> {
  const { field, headerName, mask, valueFormatter, ...rest } = options;

  return {
    field,
    headerName,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: valueFormatter ?? ((value: unknown) => applyMask(value, mask)),
    ...rest,
  };
}

export type BooleanColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  /** When provided, renders an interactive checkbox that calls back with the row. */
  onToggle?: (row: R, checked: boolean) => void;
};

/** Boolean / checkbox data column (not row selection). */
export function booleanColumn<R extends GridValidRowModel>(options: BooleanColumnOptions<R>): GridColDef<R> {
  const { field, headerName, onToggle, renderCell, ...rest } = options;

  return {
    field,
    headerName,
    type: 'boolean',
    sortable: false,
    renderCell:
      renderCell ??
      ((params: GridRenderCellParams<R>) => (
        <Checkbox
          size="small"
          checked={Boolean(params.value)}
          disabled={!onToggle}
          disableRipple
          slotProps={{ input: { 'aria-label': headerName } }}
          onChange={onToggle ? (event) => onToggle(params.row, event.target.checked) : undefined}
          onClick={(event) => event.stopPropagation()}
        />
      )),
    ...rest,
  };
}

/** Theme-token tones for enum pills (no hard-coded hex). */
export type EnumTone = 'neutral' | 'positive' | 'negative' | 'warning' | 'info';

export type EnumOption = {
  value: string;
  label?: string;
  tone?: EnumTone;
};

export type EnumColumnOptions<R extends GridValidRowModel> = Partial<GridColDef<R>> & {
  field: GridColDef<R>['field'];
  headerName: string;
  options: EnumOption[];
  /** Enable inline single-select editing. */
  editable?: boolean;
  valueOptions?: ValueOptions[];
};

function EnumPill({ label, tone }: { label: string; tone: EnumTone }) {
  const theme = useTheme();

  const palette =
    tone === 'positive'
      ? theme.palette.success
      : tone === 'negative'
        ? theme.palette.error
        : tone === 'warning'
          ? theme.palette.warning
          : tone === 'info'
            ? theme.palette.info
            : null;

  const bgcolor = palette ? palette.main : theme.palette.action.selected;
  const color = palette ? contrastTextColor(bgcolor) : theme.palette.text.primary;

  return (
    <HFChip
      size="small"
      label={label}
      variant="filled"
      sx={{
        height: 22,
        fontSize: 11,
        letterSpacing: '0.02em',
        borderRadius: 1,
        fontWeight: 700,
        bgcolor,
        color,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

export function resolveEnumOption(options: EnumOption[], value: unknown): EnumOption | undefined {
  if (value == null) return undefined;
  const key = toCellString(value);
  return options.find((option) => option.value === key);
}

/**
 * Enum / status column rendered as a pill.
 * Uses `type: 'singleSelect'` so Community's `is any of` multi-select filter works.
 */
export function enumColumn<R extends GridValidRowModel>(options: EnumColumnOptions<R>): GridColDef<R> {
  const { field, headerName, options: enumOptions, editable = false, renderCell, valueOptions, ...rest } = options;

  const resolvedValueOptions: ValueOptions[] =
    valueOptions ??
    enumOptions.map((option) => ({
      value: option.value,
      label: option.label ?? option.value,
    }));

  return {
    field,
    headerName,
    type: 'singleSelect',
    editable,
    align: 'center',
    headerAlign: 'left',
    valueOptions: resolvedValueOptions,
    renderCell:
      renderCell ??
      ((params: GridRenderCellParams<R>) => {
        const option = resolveEnumOption(enumOptions, params.value);
        const label = option?.label ?? (params.value == null ? '' : String(params.value));
        if (!label) return null;
        return (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <EnumPill label={label} tone={option?.tone ?? 'neutral'} />
          </Box>
        );
      }),
    ...rest,
  };
}

export type ActionItem<R extends GridValidRowModel> = {
  icon: ReactElement;
  label: string;
  onClick: (row: R) => void;
  /** Theme color for the icon button (`error` for delete). */
  color?: 'default' | 'inherit' | 'primary' | 'error';
  /** Place the action in the overflow menu instead of an inline icon. */
  inMenu?: boolean;
  hidden?: (row: R) => boolean;
  disabled?: (row: R) => boolean;
};

export type ActionsColumnOptions<R extends GridValidRowModel> = {
  field?: string;
  headerName?: string;
  width?: number;
  actions: Array<ActionItem<R>>;
};

function tintedIcon(icon: ReactElement, error: boolean): ReactElement {
  if (!error) return icon;
  return (
    <Box component="span" sx={{ color: 'error.main', display: 'inline-flex' }}>
      {icon}
    </Box>
  );
}

/**
 * Action column (`type: 'actions'`): inline icon buttons, delete, multi-icon,
 * and overflow dropdown via `inMenu: true` → `showInMenu`.
 */
export function actionsColumn<R extends GridValidRowModel>(options: ActionsColumnOptions<R>): GridColDef<R> {
  const { field = 'actions', headerName = '', width = 100, actions } = options;

  const col: GridActionsColDef<R> = {
    field,
    headerName,
    type: 'actions',
    width,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    getActions: (params: GridRowParams<R>) =>
      actions
        .filter((action) => !action.hidden?.(params.row))
        .map((action) => {
          const icon = tintedIcon(action.icon, action.color === 'error');
          if (action.inMenu) {
            return (
              <GridActionsCellItem
                key={action.label}
                icon={icon}
                label={action.label}
                showInMenu
                disabled={action.disabled?.(params.row)}
                onClick={() => action.onClick(params.row)}
              />
            );
          }
          return (
            <GridActionsCellItem
              key={action.label}
              icon={icon}
              label={action.label}
              disabled={action.disabled?.(params.row)}
              color={action.color === 'primary' ? 'primary' : action.color === 'inherit' ? 'inherit' : 'default'}
              onClick={() => action.onClick(params.row)}
            />
          );
        }),
  };

  return col;
}
